import * as React from 'react';
import { createContext, useEffect, useState } from 'react';

import type { ApolloError, ApolloQueryResult } from 'apollo-client';

import type { ProfileInfoField } from 'bundles/ai-coach-platform/components/patterns/chat/settings/types';
import { ChatHistoryStatus, SettingsView } from 'bundles/ai-coach-platform/components/patterns/chat/settings/types';
import useCoachSessions from 'bundles/ai-coach-platform/components/patterns/chat/settings/useCoachSessions';
import useCoachSettings from 'bundles/ai-coach-platform/components/patterns/chat/settings/useCoachSettings';
import { SettingsNotificationProvider } from 'bundles/ai-coach-platform/components/patterns/chat/settings/useSettingsNotification';
import type { AiCoachPlatform_SearchCoachMemoryQuery as SearchCoachMemoryQuery } from 'bundles/ai-coach-platform/queries/__generated__/aiCoachSearchMemories';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';

type ProfileInfoFields = Record<
  ProfileInfoField,
  {
    enabled: boolean;
  }
>;

export type CoachSettings = {
  /** settings data per settings view page */
  data: {
    [SettingsView.PROFILE_DATA]?: {
      enabled: boolean;
      fields?: ProfileInfoFields;
    };
    [SettingsView.CHAT_HISTORY]?: {
      status: ChatHistoryStatus;
    };
    [SettingsView.MEMORY]?: {
      enabled: boolean;
    };
  };

  /** whether any settings data is being loaded */
  loading: boolean;

  /** update settings data */
  updateData: (data: CoachSettings['data']) => void;

  error?: ApolloError | null;

  memories?: {
    fetchMoreMemories: () => Promise<ApolloQueryResult<SearchCoachMemoryQuery>> | Promise<void>;
    totalMemories: number;
  };

  /** whether continuous chat is enabled */
  enableContinuousChat?: boolean;

  /** brand label for the chat (e.g. 'Coach' or 'Coursera AI') */
  brandLabel?: string;
};

const CoachSettingsContext = createContext<CoachSettings>({
  data: {},
  loading: true,
  updateData: (data) => undefined,
  memories: {
    fetchMoreMemories: () => Promise.resolve(),
    totalMemories: 0,
  },
  brandLabel: 'Coach',
});

const SettingsContextProvider = ({
  children,
  settingsEnabled,
  enableContinuousChat = false,
  brandLabel = 'Coach',
}: {
  children: React.ReactNode;
  settingsEnabled: boolean;
  enableContinuousChat?: boolean;
  brandLabel?: string;
}) => {
  const [settingsData, setSettingsData] = useState<CoachSettings['data']>({});
  const { data, loading: settingsLoading, error: settingsFetchError } = useCoachSettings({ settingsEnabled });
  const { sessions, loading: sessionsLoading, error: sessionsFetchError } = useCoachSessions({ settingsEnabled });
  const [error, setError] = useState<ApolloError | null>(null);

  useEffect(() => {
    if (!settingsLoading) {
      setSettingsData((prevData) => ({
        ...prevData,
        [SettingsView.PROFILE_DATA]: {
          enabled: data.profilePreference.enableProfile,
          fields: {
            educationHistory: {
              enabled: data.profilePreference.enableEducationHistory,
            },
            workHistory: { enabled: data.profilePreference.enableWorkHistory },
            location: { enabled: data.profilePreference.enableLocation },
          },
        },
        [SettingsView.MEMORY]: {
          enabled: data.memoryPreference.enableMemory,
        },
      }));
    }

    if (!sessionsLoading) {
      setSettingsData((prevData) => ({
        ...prevData,
        [SettingsView.CHAT_HISTORY]: {
          status: sessions?.length ? ChatHistoryStatus.SESSIONS_AVAILABLE : ChatHistoryStatus.SESSIONS_DELETED,
        },
      }));
    }

    if (settingsFetchError) {
      setError(settingsFetchError);
      captureMessageInSentry({ error: settingsFetchError, source: 'SettingsContextProvider' });
    }

    if (sessionsFetchError) {
      setError(sessionsFetchError);
      captureMessageInSentry({ error: sessionsFetchError, source: 'SettingsContextProvider' });
    }
  }, [
    settingsLoading,
    settingsFetchError,
    sessionsLoading,
    sessionsFetchError,
    data.profilePreference.enableEducationHistory,
    data.profilePreference.enableLocation,
    data.profilePreference.enableProfile,
    data.profilePreference.enableWorkHistory,
    data.memoryPreference,
    sessions?.length,
  ]);

  const updateData = (newData: CoachSettings['data']) => {
    setSettingsData(newData);
  };

  return (
    <CoachSettingsContext.Provider
      value={{
        data: settingsData,
        loading: settingsLoading || sessionsLoading,
        updateData,
        error,
        enableContinuousChat,
        brandLabel,
      }}
    >
      <SettingsNotificationProvider>{children}</SettingsNotificationProvider>
    </CoachSettingsContext.Provider>
  );
};

export { CoachSettingsContext, SettingsContextProvider };
